import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
// The video intro card (ADR-036 decision 5), ported from mockups/kit/components/intro.js: the card a
// player draws over the video's stage before a title starts. It is built from the title's metadata as
// real text (a labelled section, the topic a heading), never an image of text, and it is not part of
// the media file.
//
//   - Lines, top to bottom: GUIDE, SECTION, TOPIC (the largest text, which wraps inside the right
//     margin), then the footer: the version line and, on its own line, the guide edition. A line whose
//     value is missing is left out; nothing is ever drawn as a placeholder.
//   - It stays INTRO_MS, then calls onDone("timeout"). "Skip intro" and the Escape key call
//     onDone("skip") at once. There is no pause control. Focus moves to Skip when the card appears;
//     onDone says whether the focus was on the card, so the owner of the Play control can take it back.
//   - The Escape listener lives only as long as the card does. It acts only while focus is inside the
//     card or its player (the element that wraps the video); Escape pressed elsewhere on the page is left
//     alone. It also leaves the key alone when something else has already handled it (a dialog's own
//     dismiss prevents the default) or when a dialog is open over the card.
//   - Under prefers-reduced-motion the card shows and hides with no fade and no progress sweep.
//   - The brand colour is the runtime theme's (--intro-brand, intro-card.css); the logo slot is the
//     `brand` prop, and an absent one leaves the slot empty.
//
// The words are the caller's: beat-player.tsx passes them from the surface catalogue, and the web
// app's review screen from its own (apps/web/src/pages/content/intro-card.tsx). The component takes
// no string of its own.
import { useEffect, useLayoutEffect, useRef, useState, } from "react";
import { t } from "../../i18n/t.js";
import { themeBrand } from "../../theme.js";
import "./intro-card.css";
/** How long the card stays, fade included. */
export const INTRO_MS = 3500;
/** The fade-out at the end, inside INTRO_MS. */
const FADE_MS = 220;
const clean = (v) => v === undefined ? "" : v.replace(/\s+/g, " ").trim();
/** Whether the card has anything to show: a card of nothing is not drawn. */
export function hasIntro(content) {
    return [content.guide, content.section, content.topic, content.version, content.edition].some((v) => clean(v) !== "");
}
const reducedMotion = () => {
    try {
        return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    }
    catch {
        return false;
    }
};
/** A dialog open somewhere other than the card: Escape is its to handle. */
function dialogOpenOver(card) {
    const dialogs = document.querySelectorAll('[role="dialog"], [role="alertdialog"], dialog[open]');
    return Array.from(dialogs).some((d) => !(card?.contains(d) ?? false) && !d.contains(card));
}
function Mark({ shapes }) {
    return (_jsx("svg", { viewBox: "0 0 24 24", "aria-hidden": true, focusable: "false", className: "wp-intro__mark", fill: "none", stroke: "currentColor", strokeWidth: 1.75, strokeLinecap: "round", strokeLinejoin: "round", children: shapes.map((shape, i) => {
            const paint = shape.filled === true ? { fill: "currentColor", stroke: "none" } : {};
            return shape.kind === "circle" ? (_jsx("circle", { cx: shape.cx, cy: shape.cy, r: shape.r, ...paint }, i)) : (_jsx("path", { d: shape.d, ...paint }, i));
        }) }));
}
function Logo({ brand }) {
    if (brand === null)
        return null;
    if (brand.kind === "image")
        return (_jsx("span", { className: "wp-intro__logo", children: _jsx("img", { className: "wp-intro__logo-img", src: brand.src, alt: brand.alt }) }));
    return (_jsxs("span", { className: "wp-intro__logo wp-intro__logo--mark", children: [_jsx(Mark, { shapes: brand.shapes }), _jsx("span", { className: "wp-intro__wordmark", children: brand.name })] }));
}
export function IntroCard({ content, labels, brand, level = 3, focus = true, ms = INTRO_MS, onDone, }) {
    const card = useRef(null);
    const skip = useRef(null);
    const [calm] = useState(reducedMotion);
    const [leaving, setLeaving] = useState(false);
    // The latest callback, so the effect below runs once for the card's life.
    const done = useRef(onDone);
    done.current = onDone;
    /** Ends the card: the timer, Escape and Skip all come through here, once. */
    const finish = useRef(() => undefined);
    const shown = hasIntro(content);
    useEffect(() => {
        if (!shown)
            return;
        let finished = false;
        let timer;
        const end = (reason) => {
            if (finished)
                return;
            finished = true;
            clearTimeout(timer);
            document.removeEventListener("keydown", onKey, true);
            const hadFocus = card.current?.contains(document.activeElement) ?? false;
            done.current({ reason, hadFocus });
        };
        function onKey(e) {
            if (e.key !== "Escape" || e.defaultPrevented || dialogOpenOver(card.current))
                return;
            // Only with focus in the card or its player (the element the card sits in, which wraps the
            // video): Escape pressed anywhere else on the page is not the card's.
            const scope = card.current?.parentElement ?? card.current;
            if (scope === null || !scope.contains(document.activeElement))
                return;
            e.preventDefault();
            e.stopPropagation();
            end("skip");
        }
        finish.current = end;
        document.addEventListener("keydown", onKey, true);
        if (focus)
            skip.current?.focus({ preventScroll: true });
        // The fade-out is part of the time: the card has ms in all.
        timer = setTimeout(() => {
            if (calm) {
                end("timeout");
                return;
            }
            setLeaving(true);
            timer = setTimeout(() => {
                end("timeout");
            }, FADE_MS);
        }, calm ? ms : Math.max(0, ms - FADE_MS));
        return () => {
            // Unmounted by its owner: nothing is reported, and nothing is left listening.
            finished = true;
            clearTimeout(timer);
            document.removeEventListener("keydown", onKey, true);
        };
        // The card's life is one run: a change of props mid-card is not a new card.
    }, [shown]);
    if (!shown)
        return null;
    const topic = clean(content.topic);
    const size = topic.length > 90 ? "xlong" : topic.length > 55 ? "long" : "normal";
    const Heading = `h${String(level)}`;
    const rows = [
        ["guide", labels.guide, clean(content.guide)],
        ["section", labels.section, clean(content.section)],
        ["topic", labels.topic, topic],
    ];
    const version = clean(content.version);
    const edition = clean(content.edition);
    const style = { "--intro-ms": `${String(ms)}ms` };
    return (_jsxs("section", { ref: card, className: `wp-intro${calm ? " is-calm" : ""}${leaving ? " is-leaving" : ""}`, "data-size": size, "data-intro": "", "aria-label": labels.region, style: style, children: [_jsx("div", { className: "wp-intro__rule", "aria-hidden": true }), _jsx(Logo, { brand: brand }), rows.some((r) => r[2] !== "") ? (_jsx("dl", { className: "wp-intro__body", children: rows.map(([key, label, value]) => value === "" ? null : (_jsxs("div", { className: `wp-intro__row wp-intro__row--${key}`, children: [_jsx("dt", { className: "wp-intro__label", children: label }), _jsx("dd", { className: "wp-intro__dd", children: key === "topic" ? (_jsx(Heading, { className: "wp-intro__value wp-intro__value--topic", children: value })) : (_jsx("span", { className: "wp-intro__value", children: value })) })] }, key))) })) : null, version !== "" || edition !== "" ? (_jsxs("footer", { className: "wp-intro__foot", children: [version === "" ? null : _jsx("p", { className: "wp-intro__ver", children: version }), edition === "" ? null : _jsx("p", { className: "wp-intro__ed", children: edition })] })) : null, _jsxs("button", { ref: skip, type: "button", className: "wp-intro__skip", onClick: () => {
                    finish.current("skip");
                }, children: [labels.skip, _jsx("svg", { viewBox: "0 0 16 16", "aria-hidden": true, className: "wp-intro__skip-ico", children: _jsx("path", { d: "M3 3l5 5-5 5M9 3l5 5-5 5", fill: "none", stroke: "currentColor", strokeWidth: 1.6, strokeLinecap: "round", strokeLinejoin: "round" }) })] }), calm ? null : _jsx("div", { className: "wp-intro__sweep", "aria-hidden": true })] }));
}
/**
 * The card as the beat player draws it: the words from the surface catalogue, the lines from the title
 * (library, section, title, version and publish date, edition), and the logo slot from the surface theme
 * the player sits in (the nearest data-theme, as applyTheme sets it).
 */
export function BeatIntro({ title, focus, onDone, }) {
    const probe = useRef(null);
    const [brand, setBrand] = useState(null);
    useLayoutEffect(() => {
        const themed = probe.current?.closest("[data-theme]")?.getAttribute("data-theme");
        const theme = themeBrand(themed);
        setBrand({ kind: "mark", shapes: theme.logo, name: theme.productName });
    }, []);
    return (_jsxs(_Fragment, { children: [_jsx("span", { ref: probe, hidden: true }), _jsx(IntroCard, { content: introContent(title), labels: {
                    region: t("intro.region"),
                    guide: t("intro.guide"),
                    section: t("intro.section"),
                    topic: t("intro.topic"),
                    skip: t("intro.skip"),
                }, brand: brand, focus: focus, onDone: onDone })] }));
}
/** "3 November 2026", in UTC so it reads the same wherever it is opened. */
const DAY = new Intl.DateTimeFormat("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: "UTC",
});
/** What the learner's card says of a title; a field the title lacks is a line left out. */
export function introContent(title) {
    const number = title.versionNumber;
    const version = number === undefined
        ? undefined
        : title.publishedAt === undefined
            ? t("intro.version", { number })
            : t("intro.versionPublished", {
                number,
                date: DAY.format(new Date(title.publishedAt)),
            });
    return {
        guide: title.library,
        section: title.section,
        topic: title.title,
        version,
        edition: title.guideEdition === undefined
            ? undefined
            : t("intro.edition", { edition: title.guideEdition }),
    };
}
//# sourceMappingURL=intro-card.js.map